<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="hint-badge"
      :style="positionStyle"
      :class="{
        'hint-badge--highlighted': highlighted,
        'hint-badge--dimmed': !highlighted
      }"
    >
      <span class="hint-badge__matched">{{ matchedPortion }}</span>
      <span class="hint-badge__remaining">{{ remainingPortion }}</span>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * HintBadge Component
 *
 * Renders a Vimium-style yellow badge for keyboard navigation hints.
 * Teleported to body and positioned absolutely based on target element.
 *
 * Features:
 * - Shows letter/number hint label
 * - Highlights matched portion when typing
 * - Dims non-matching hints
 * - Positioning based on hint type:
 *   - 'dropzone': Centered on the dropzone element
 *   - 'node': Top-left corner of the node (like Vimium browser extension)
 */

import { computed, CSSProperties } from 'vue'

interface Props {
  /** The hint label to display (e.g., 'A', 'AB', 'S') */
  label: string
  /** The target element's bounding rect for positioning */
  targetRect: DOMRect
  /** Currently typed input buffer for partial matching */
  inputBuffer?: string
  /** Whether the badge should be visible */
  visible?: boolean
  /** Type of hint - affects positioning strategy */
  hintType?: 'dropzone' | 'node'
}

const props = withDefaults(defineProps<Props>(), {
  inputBuffer: '',
  visible: true,
  hintType: 'dropzone'
})

/**
 * Whether this hint is highlighted (matches current input)
 */
const highlighted = computed(() => {
  if (!props.inputBuffer) return true
  return props.label.startsWith(props.inputBuffer)
})

/**
 * The portion of the label that matches the input (shown in bold)
 */
const matchedPortion = computed(() => {
  if (!props.inputBuffer || !highlighted.value) return ''
  return props.label.slice(0, props.inputBuffer.length)
})

/**
 * The remaining portion of the label after the matched part
 */
const remainingPortion = computed(() => {
  if (!props.inputBuffer || !highlighted.value) return props.label
  return props.label.slice(props.inputBuffer.length)
})

/**
 * Computed position style based on target element and hint type
 * - Dropzones: Centered on the element
 * - Nodes: Top-left corner with slight inset (like Vimium)
 */
const positionStyle = computed<CSSProperties>(() => {
  const { targetRect, hintType } = props

  if (hintType === 'node') {
    // Top-left corner positioning for nodes (like Vimium browser extension)
    return {
      position: 'fixed',
      left: `${targetRect.left + 8}px`,
      top: `${targetRect.top + 8}px`,
      zIndex: 40
    }
  }

  // Default: centered positioning for dropzones
  return {
    position: 'fixed',
    left: `${targetRect.left + targetRect.width / 2 - 10}px`,
    top: `${targetRect.top + targetRect.height / 2 - 10}px`,
    zIndex: 40
  }
})
</script>

<style scoped>
.hint-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 20px;
  padding: 0 4px;
  border-radius: 3px;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  pointer-events: none;
  user-select: none;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(0, 0, 0, 0.1);
  transition: opacity 0.15s ease;
}

.hint-badge--highlighted {
  background: linear-gradient(180deg, #fef08a 0%, #fde047 100%);
  color: #1f2937;
  border: 1px solid #ca8a04;
}

.hint-badge--dimmed {
  background: linear-gradient(180deg, #d1d5db 0%, #9ca3af 100%);
  color: #4b5563;
  border: 1px solid #6b7280;
  opacity: 0.5;
}

.hint-badge__matched {
  color: #dc2626;
  font-weight: 800;
}

.hint-badge__remaining {
  font-weight: 700;
}

/* Dark mode support */
:root.dark .hint-badge--highlighted {
  background: linear-gradient(180deg, #fef08a 0%, #facc15 100%);
  color: #111827;
  border-color: #a16207;
}

:root.dark .hint-badge--dimmed {
  background: linear-gradient(180deg, #4b5563 0%, #374151 100%);
  color: #9ca3af;
  border-color: #6b7280;
  opacity: 0.4;
}
</style>
